import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Form } from 'antd';
import dayjs from 'dayjs';
import { Frequency, RRuleSet, ALL_WEEKDAYS, type WeekdayStr } from 'rrule';
import type { FieldError } from 'rc-field-form/lib/interface';
import type {
  ScheduleUpsertModalB50ContextValue,
  ScheduleUpsertModalB50FormValues,
  SelectedScheduleRouteRoutine,
  UseScheduleUpsertModalB50Params,
} from '../types/MachineDetailsRobotSchedule.types';
import { ScheduleUpdateScope } from '../types/MachineDetailsRobotSchedule.types';
import {
  buildFallbackRouteRoutineSelection,
  formatRoutineSelectLabel,
  getSelectedRouteRoutineById,
  mapScheduleRoutesToSelectOptions,
  mapScheduleRoutinesToSelectOptions,
} from '../utils/routeRoutine';
import { buildScheduleRRuleSet, parseRRuleString, rebaseRRuleStart } from '../utils/rruleDateTime';
import {
  combineScheduleDateAndTime,
  isScheduleDateTimeInThePast,
  parseIso8601DurationMinutes,
} from '../utils/schedule';
import { DEFAULT_CLEANING_DURATION_MINUTES, DEFAULT_CHARGING_DURATION_MINUTES } from '../utils/constants';
import {
  OrderDirection,
  RouteOrderField,
  RoutineOrderField,
  type UpdateScheduleInput,
} from 'app/cross-cutting-concerns/communication/interfaces/pra-api-graphql';
import type { ScheduleServiceCreateScheduleOptions } from 'app/modules/machine-inventory/ScheduleService';
import { RobotScheduleActions } from 'app/modules/machine-inventory/machine-details-panel/state/machineDetailsPanelRobot/robotSchedule/robotScheduleSlice';
import * as robotScheduleSelectors from 'app/modules/machine-inventory/machine-details-panel/state/machineDetailsPanelRobot/robotSchedule/robotScheduleSelectors';
import { DateTime } from 'lib/utils/date-handling/DateTime';
import { selectCreateScheduleData } from 'app/modules/machine-inventory/machine-details-panel/state/machineDetailsPanelRobot/robotSchedule/robotScheduleSelectors';
import { toast } from 'app/cross-cutting-concerns/toasts/kcc/toast';

export const useScheduleUpsertModalB50State = ({
  robot,
  onClose,
  isOpen,
  isEventOpen,
  selectedEvent,
  selectedCreateDateTime,
  updateScope,
  handleOpenUpdateConfirmModal,
}: UseScheduleUpsertModalB50Params): ScheduleUpsertModalB50ContextValue => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const [form] = Form.useForm<ScheduleUpsertModalB50FormValues>();
  const [isRepeating, setIsRepeating] = useState<boolean>(false);
  const [hasErrorScheduleName, setHasErrorScheduleName] = useState<boolean>(false);
  const [hasErrorScheduleRouteRoutine, setHasErrorScheduleRouteRoutine] = useState<boolean>(false);
  const [isEditingScheduleName, setIsEditingScheduleName] = useState<boolean>(false);
  const [scheduleRouteRoutine, setScheduleRouteRoutine] = useState<SelectedScheduleRouteRoutine | undefined>(undefined);
  const [isDropdownVisible, setIsDropdownVisible] = useState(false);
  const [hasScheduleChanges, setHasScheduleChanges] = useState(false);
  const [scheduleHasChanged, setScheduleHasChanged] = useState<Record<string, boolean>>({});
  const [isOriginalEventRepeating, setIsOriginalEventRepeating] = useState<boolean>(false);
  const editFormValuesRef = useRef<ScheduleUpsertModalB50FormValues | null>(null);
  const createScheduleData = useSelector(selectCreateScheduleData);
  const scheduleTime = Form.useWatch('scheduleTime', form) || dayjs();
  const watchedScheduleName = Form.useWatch('scheduleName', form);
  const scheduleName = watchedScheduleName || selectedEvent?.resource.scheduleEvent.name || '-';
  const isScheduleNameInvalid = !watchedScheduleName?.trim();

  useEffect(() => {
    if (isEventOpen) {
      setIsEditingScheduleName(false);
    }
  }, [isEventOpen]);

  const handleToggleEditingScheduleName = useCallback((): void => {
    setIsEditingScheduleName(prev => !prev);
  }, []);

  useEffect(() => {
    // Robot A's route/routine must not leak into robot B's form when the panel is reused for a different robot.
    setScheduleRouteRoutine(undefined);
    form.setFieldsValue({ scheduleRouteRoutine: undefined });
    dispatch(RobotScheduleActions.robotScheduleListRoutinesReset());
    dispatch(RobotScheduleActions.robotScheduleListRoutesReset());
    dispatch(
      RobotScheduleActions.robotScheduleListRoutinesRequest({
        materialNumber: robot.materialNumber,
        serialNumber: robot.serialNumber,
        anonymous: false,
        first: 1000,
        orderBy: { field: RoutineOrderField.Name, direction: OrderDirection.Asc },
      })
    );
    dispatch(
      RobotScheduleActions.robotScheduleListRoutesRequest({
        materialNumber: robot.materialNumber,
        serialNumber: robot.serialNumber,
        first: 1000,
        orderBy: { field: RouteOrderField.Name, direction: OrderDirection.Asc },
      })
    );
  }, [dispatch, form, robot.materialNumber, robot.serialNumber]);

  const rawScheduleRoutines = useSelector(robotScheduleSelectors.selectRobotScheduleRoutines);
  const rawScheduleRoutes = useSelector(robotScheduleSelectors.selectRobotScheduleRoutes);
  const scheduleRoutines = useMemo(() => rawScheduleRoutines ?? [], [rawScheduleRoutines]);
  const scheduleRoutes = useMemo(() => rawScheduleRoutes ?? [], [rawScheduleRoutes]);

  // Anonymous routines are excluded from the picker by the API (anonymous: false). A schedule
  // already assigned to an anonymous routine still resolves and displays correctly when editing —
  // see the routine/route resolution below, which reads straight off the occurrence rather than
  // this (anonymous-free) list.
  const unnamedRoutineFallback = t('createSchedule.unnamedRoutine');
  const scheduleRoutineOptions = useMemo(
    () =>
      mapScheduleRoutinesToSelectOptions({
        routines: scheduleRoutines,
        routes: scheduleRoutes,
        unnamedRoutineFallback,
      }),
    [scheduleRoutines, scheduleRoutes, unnamedRoutineFallback]
  );
  const scheduleRouteOptions = useMemo(() => mapScheduleRoutesToSelectOptions(scheduleRoutes), [scheduleRoutes]);
  const isLoadingScheduleRoutines = useSelector(robotScheduleSelectors.selectRobotScheduleRoutinesIsLoading);
  const isLoadingScheduleRoutes = useSelector(robotScheduleSelectors.selectRobotScheduleRoutesIsLoading);
  const isLoadingScheduleRouteRoutine = isLoadingScheduleRoutines || isLoadingScheduleRoutes;

  const expectedCompletionTime = useMemo(() => {
    let durationInNumber = 0;
    let postChargingDurationInNumber = 0;

    if (scheduleRouteRoutine?.type === 'routine') {
      durationInNumber += parseIso8601DurationMinutes(
        scheduleRouteRoutine.routine?.duration,
        DEFAULT_CLEANING_DURATION_MINUTES
      );
      postChargingDurationInNumber += parseIso8601DurationMinutes(
        scheduleRouteRoutine.routine?.postChargingDuration,
        DEFAULT_CHARGING_DURATION_MINUTES
      );
    }

    if (scheduleRouteRoutine?.type === 'route') {
      durationInNumber += parseIso8601DurationMinutes(
        scheduleRouteRoutine.route?.duration,
        DEFAULT_CLEANING_DURATION_MINUTES
      );
      postChargingDurationInNumber += parseIso8601DurationMinutes(
        scheduleRouteRoutine.route?.postChargingDuration,
        DEFAULT_CHARGING_DURATION_MINUTES
      );
    }

    return {
      finishCleaningTime: dayjs(scheduleTime).add(durationInNumber, 'minutes'),
      fullyChargedTime: dayjs(scheduleTime).add(durationInNumber + postChargingDurationInNumber, 'minutes'),
    };
  }, [scheduleRouteRoutine, scheduleTime]);

  const handleScheduleRouteRoutineChange = useCallback(
    (selectedScheduleRouteRoutine: SelectedScheduleRouteRoutine | undefined): void => {
      setScheduleRouteRoutine(selectedScheduleRouteRoutine);
    },
    []
  );

  const handleDropdownVisibleChange = useCallback((visible: boolean): void => {
    setIsDropdownVisible(visible);
  }, []);

  const handleEditScheduleRouteRoutine = useCallback((): void => {
    setScheduleRouteRoutine(undefined);
    form.resetFields(['scheduleRouteRoutine']);
    setIsDropdownVisible(true);

    const baseline = editFormValuesRef.current;
    if (baseline) {
      setScheduleHasChanged(prev => ({ ...prev, scheduleRouteRoutine: baseline.scheduleRouteRoutine !== undefined }));
    }
  }, [form]);

  const handleRepeatingChange = useCallback(
    (checked: boolean): void => {
      const baseline = editFormValuesRef.current;
      setIsRepeating(checked);

      if (!checked) {
        form.setFieldsValue({
          scheduleFrequency: Frequency.WEEKLY,
          scheduleEvery: 1,
          weekdays: [],
          scheduleEndDate: undefined,
        });
      }

      if (baseline) {
        setScheduleHasChanged(prev => ({ ...prev, isRepeating: baseline.isRepeating !== checked }));
      }
    },
    [form]
  );

  const handleWeekdaysChange = useCallback(
    (checked: boolean, tag: WeekdayStr): void => {
      const weekdays = form.getFieldValue('weekdays') ?? [];
      const nextSelectedTags = checked ? [...weekdays, tag] : weekdays.filter((day: WeekdayStr) => day !== tag);
      form.setFieldsValue({
        weekdays: nextSelectedTags,
      });

      const baseline = editFormValuesRef.current;
      if (baseline) {
        setScheduleHasChanged(prev => ({
          ...prev,
          weekdays: baseline.weekdays?.toString() !== nextSelectedTags.toString(),
        }));
      }
    },
    [form]
  );

  const handleFormValuesChange = useCallback(
    (changedValues: ScheduleUpsertModalB50FormValues): void => {
      if (isEventOpen) {
        Object.entries(changedValues).forEach(([key, value]) => {
          const baseline = editFormValuesRef.current;
          if (baseline) {
            if (key === 'scheduleDate' || key === 'scheduleEndDate') {
              setScheduleHasChanged(prev => ({
                ...prev,
                [key]: baseline[key]?.format('YYYY-MM-DD') !== value.format('YYYY-MM-DD'),
              }));
            } else if (key === 'scheduleTime') {
              setScheduleHasChanged(prev => ({
                ...prev,
                [key]: baseline[key].format('HH:mm') !== value.format('HH:mm'),
              }));
            } else {
              setScheduleHasChanged(prev => ({
                ...prev,
                [key]: baseline[key as keyof ScheduleUpsertModalB50FormValues] !== value,
              }));
            }
          }
        });
      }
    },
    [isEventOpen]
  );

  const handleValidateFields = useCallback(
    (fields?: string[]): void => {
      const fieldsToValidate = fields ?? ['scheduleName', 'scheduleRouteRoutine'];
      form
        .validateFields(fieldsToValidate)
        .then(() => {
          if (fieldsToValidate.includes('scheduleName')) {
            setHasErrorScheduleName(false);
          }
          if (fieldsToValidate.includes('scheduleRouteRoutine')) {
            setHasErrorScheduleRouteRoutine(false);
          }
        })
        .catch(error => {
          const errorFieldNames: string[] = (error?.errorFields ?? []).map(
            (errorField: FieldError) => errorField.name[0]
          );
          if (errorFieldNames.includes('scheduleName')) {
            setHasErrorScheduleName(true);
          }
          if (errorFieldNames.includes('scheduleRouteRoutine')) {
            setHasErrorScheduleRouteRoutine(true);
          }
        });
    },
    [form]
  );

  const handleResetState = useCallback((): void => {
    setIsRepeating(false);
    setIsOriginalEventRepeating(false);
    setHasErrorScheduleName(false);
    setHasErrorScheduleRouteRoutine(false);
    setScheduleRouteRoutine(undefined);
    setHasScheduleChanges(false);
    setScheduleHasChanged({});
    editFormValuesRef.current = null;
  }, []);

  const resetFormState = useCallback((): void => {
    form.resetFields();
    handleResetState();
  }, [form, handleResetState]);

  const handleCancel = useCallback((): void => {
    onClose();
  }, [onClose]);

  const handleSaveChanges = useCallback((): void => {
    form
      .validateFields()
      .then(() => {
        setHasErrorScheduleName(false);
        setHasErrorScheduleRouteRoutine(false);

        if (isOriginalEventRepeating) {
          handleOpenUpdateConfirmModal();
          return;
        }

        form.submit();
      })
      .catch(error => {
        const errorFieldNames: string[] = (error?.errorFields ?? []).map(
          (errorField: FieldError) => errorField.name[0]
        );
        setHasErrorScheduleName(errorFieldNames.includes('scheduleName'));
        setHasErrorScheduleRouteRoutine(errorFieldNames.includes('scheduleRouteRoutine'));
      });
  }, [form, isOriginalEventRepeating, handleOpenUpdateConfirmModal]);

  const buildRRuleSet = useCallback(
    (values: ScheduleUpsertModalB50FormValues): RRuleSet => buildScheduleRRuleSet({ values, isRepeating }),
    [isRepeating]
  );

  const handleSubmit = useCallback(
    (values: ScheduleUpsertModalB50FormValues): void => {
      const occurrenceStart = combineScheduleDateAndTime(values.scheduleDate, values.scheduleTime);

      if (isScheduleDateTimeInThePast(occurrenceStart)) {
        toast.error({
          message: t(
            isEventOpen
              ? 'machineDetails.robotSchedule.updateForbiddenInThePast'
              : 'machineDetails.robotSchedule.createForbiddenInThePast'
          ),
          className: 'kcc-toast--message-regular',
        });
        return;
      }

      const routeId = scheduleRouteRoutine?.type === 'route' ? scheduleRouteRoutine.route.id : undefined;
      const routineId = scheduleRouteRoutine?.type === 'routine' ? scheduleRouteRoutine.routine.id : undefined;

      if (!isEventOpen) {
        const createInput: ScheduleServiceCreateScheduleOptions = {
          materialNumber: robot.materialNumber,
          serialNumber: robot.serialNumber,
          routeId,
          routineId,
          rrule: buildRRuleSet(values).toString(),
          name: scheduleName,
        };

        dispatch(RobotScheduleActions.createScheduleRequest(createInput));
        return;
      }

      const occurrence = selectedEvent?.resource.scheduleEvent.occurrence;
      const scheduleId = occurrence?.scheduleId;

      if (!scheduleId || !occurrence) {
        return;
      }

      const hasRruleChanges = (
        [
          'scheduleDate',
          'scheduleTime',
          'scheduleEndDate',
          'scheduleFrequency',
          'scheduleEvery',
          'weekdays',
          'isRepeating',
        ] as const
      ).some(key => scheduleHasChanged[key]);
      const isThisAndFollowing = updateScope === ScheduleUpdateScope.ThisAndFollowing;
      // Resending the series' original DTSTART would make the split schedule restart at the series start
      const getRRule = (): string => {
        if (hasRruleChanges) return buildRRuleSet(values).toString();
        if (!isThisAndFollowing) return occurrence.rrule;

        return rebaseRRuleStart({ sourceRRule: occurrence.rrule, occurrenceStart: occurrence.startDate }).toString();
      };
      const rrule = getRRule();

      const updateInput: UpdateScheduleInput = {
        id: scheduleId,
        name: watchedScheduleName,
        routeId,
        routineId,
        rrule,
        occurrence: occurrence.startDate,
        applyToFollowing: isThisAndFollowing,
      };

      dispatch(RobotScheduleActions.updateScheduleRequest(updateInput));
    },
    [
      robot,
      scheduleRouteRoutine,
      scheduleName,
      dispatch,
      isEventOpen,
      buildRRuleSet,
      updateScope,
      selectedEvent,
      watchedScheduleName,
      scheduleHasChanged,
      t,
    ]
  );

  useEffect(() => {
    if (isOpen) {
      const defaultScheduleDate = selectedCreateDateTime ?? dayjs().add(1, 'hour').startOf('hour');
      resetFormState();

      form.setFieldsValue({
        scheduleName: undefined,
        scheduleDate: defaultScheduleDate,
        scheduleTime: defaultScheduleDate,
        scheduleRouteRoutine: undefined,
        scheduleFrequency: Frequency.WEEKLY,
        scheduleEvery: 1,
        weekdays: [],
        scheduleEndDate: undefined,
      });
    }
  }, [form, isOpen, selectedCreateDateTime, resetFormState]);

  useEffect(() => {
    if (isEventOpen && selectedEvent) {
      const timezone = DateTime.getBrowserTimeZone();
      const occurrence = selectedEvent.resource.scheduleEvent.occurrence;
      const { until, byweekday, freq, interval, count } = parseRRuleString(occurrence.rrule);
      const eventScheduleFrequency: Frequency = freq;
      const eventScheduleEvery = interval;
      const eventWeekdays: WeekdayStr[] = (byweekday ?? []).map(weekday => ALL_WEEKDAYS[weekday]);
      const eventScheduleEndDate = until ? dayjs.tz(until, timezone) : undefined;
      const eventIsRepeating = count !== 1;

      const eventScheduleName = selectedEvent.resource.scheduleEvent.name;
      const { startDate, route, routine } = occurrence;
      const eventScheduleRouteRoutineId = route?.id || routine?.id || '';
      const selectedScheduleRouteRoutine = eventScheduleRouteRoutineId
        ? getSelectedRouteRoutineById({
            id: eventScheduleRouteRoutineId,
            routines: scheduleRoutines,
            routes: scheduleRoutes,
            unnamedRoutineFallback,
          }) ?? buildFallbackRouteRoutineSelection(occurrence)
        : undefined;
      const eventStartDate = dayjs(startDate);

      setIsRepeating(eventIsRepeating);
      setIsOriginalEventRepeating(eventIsRepeating);
      setScheduleRouteRoutine(selectedScheduleRouteRoutine);

      const formValues = {
        scheduleName: eventScheduleName,
        scheduleDate: eventStartDate,
        scheduleTime: eventStartDate,
        scheduleRouteRoutine: eventScheduleRouteRoutineId,
        scheduleFrequency: eventScheduleFrequency,
        scheduleEvery: eventScheduleEvery,
        weekdays: eventWeekdays,
        scheduleEndDate: eventScheduleEndDate,
        isRepeating: eventIsRepeating,
      };

      form.setFieldsValue(formValues);

      editFormValuesRef.current = formValues;
    } else {
      editFormValuesRef.current = null;
      setIsOriginalEventRepeating(false);
      setHasScheduleChanges(false);
      setScheduleHasChanged({});
      setScheduleRouteRoutine(undefined);
    }
    // scheduleRoutines/scheduleRoutes/unnamedRoutineFallback are intentionally excluded — see the
    // comment on the effect below for why they're kept out of this one.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form, isEventOpen, selectedEvent, robot]);

  // Resolves the selected route/routine label once routines/routes are available. Kept out of the
  // form-initialization effect above so that an async list update while the edit modal is open
  // doesn't re-run form.setFieldsValue and clobber in-progress user edits. Skips once the user has
  // explicitly changed the route/routine selection themselves.
  useEffect(() => {
    if (!isEventOpen || !selectedEvent || scheduleHasChanged.scheduleRouteRoutine) {
      return;
    }

    const { route, routine } = selectedEvent.resource.scheduleEvent.occurrence;
    const eventScheduleRouteRoutineId = route?.id || routine?.id || '';

    // Anonymous routines are excluded from the picker (anonymous: false), so they're never in
    // scheduleRoutines — resolve those straight off the occurrence instead. Everything else
    // still goes through the normal by-id lookup against the fetched lists.
    const selectedScheduleRouteRoutine: SelectedScheduleRouteRoutine | undefined = routine?.anonymous
      ? {
          type: 'routine',
          routine: {
            ...routine,
            name: formatRoutineSelectLabel({ routine, routes: scheduleRoutes, unnamedRoutineFallback }),
          },
        }
      : getSelectedRouteRoutineById({
          id: eventScheduleRouteRoutineId,
          routines: scheduleRoutines,
          routes: scheduleRoutes,
          unnamedRoutineFallback,
        });

    setScheduleRouteRoutine(selectedScheduleRouteRoutine);
  }, [
    isEventOpen,
    selectedEvent,
    scheduleRoutines,
    scheduleRoutes,
    unnamedRoutineFallback,
    scheduleHasChanged.scheduleRouteRoutine,
  ]);

  useEffect(() => {
    // Skip while routines/routes are in flight so a stale, previous-robot options list can't be
    // auto-selected before the current robot's own lists have loaded.
    const totalScheduleRouteRoutine = scheduleRoutineOptions.length + scheduleRouteOptions.length;
    if (
      isOpen &&
      !isEventOpen &&
      !isLoadingScheduleRouteRoutine &&
      totalScheduleRouteRoutine === 1 &&
      !scheduleRouteRoutine
    ) {
      if (scheduleRoutineOptions.length > 0) {
        form.setFieldsValue({ scheduleRouteRoutine: scheduleRoutineOptions[0].value });
        handleScheduleRouteRoutineChange({ type: 'routine', routine: scheduleRoutineOptions[0].routine });
      } else {
        form.setFieldsValue({ scheduleRouteRoutine: scheduleRouteOptions[0].value });
        handleScheduleRouteRoutineChange({ type: 'route', route: scheduleRouteOptions[0].route });
      }
    }
  }, [
    form,
    isOpen,
    isEventOpen,
    isLoadingScheduleRouteRoutine,
    scheduleRoutineOptions,
    scheduleRouteOptions,
    scheduleRouteRoutine,
    handleScheduleRouteRoutineChange,
  ]);

  useEffect(() => {
    setHasScheduleChanges(Object.values(scheduleHasChanged).some(value => value));
  }, [scheduleHasChanged]);

  useEffect(() => {
    if (createScheduleData) {
      handleCancel();
      dispatch(RobotScheduleActions.createScheduleReset());
    }
  }, [createScheduleData, handleCancel, dispatch]);

  return {
    form,
    robot,
    scheduleRoutineOptions,
    scheduleRouteOptions,
    isLoadingScheduleRouteRoutine,
    scheduleRouteRoutine,
    scheduleName,
    hasErrorScheduleName,
    hasErrorScheduleRouteRoutine,
    isEditingScheduleName,
    isScheduleNameInvalid,
    handleToggleEditingScheduleName,
    expectedCompletionTime,
    isRepeating,
    hasScheduleChanges,
    isDropdownVisible,
    handleDropdownVisibleChange,
    handleEditScheduleRouteRoutine,
    handleScheduleRouteRoutineChange,
    handleRepeatingChange,
    handleWeekdaysChange,
    handleFormValuesChange,
    handleValidateFields,
    handleSaveChanges,
    handleSubmit,
    handleCancel,
  };
};
